import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ROUTES } from './constants';
import theme from './theme';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Agents from './pages/Agents';
import CreateAgent from './pages/CreateAgent';
import KnowledgeBases from './pages/KnowledgeBases';
import CreateKnowledgeBase from './pages/CreateKnowledgeBase';
import KnowledgeBaseDetail from './pages/KnowledgeBaseDetail';
import DocumentUpload from './pages/DocumentUpload';
import ExternalApi from './pages/ExternalApi';
import CreateApiKey from './pages/CreateApiKey';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  return isAuthenticated ? children : <Navigate to={ROUTES.LOGIN} replace />;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  return isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route
              path={ROUTES.LOGIN}
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            
            {/* Protected Routes */}
            <Route
              path={ROUTES.DASHBOARD}
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.AGENTS}
              element={
                <ProtectedRoute>
                  <Layout>
                    <Agents />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.AGENTS_CREATE}
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateAgent />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.AGENTS_EDIT}
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateAgent />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Knowledge Base Routes */}
            <Route
              path={ROUTES.KNOWLEDGE_BASE}
              element={
                <ProtectedRoute>
                  <Layout>
                    <KnowledgeBases />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.KNOWLEDGE_BASE_CREATE}
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateKnowledgeBase />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.KNOWLEDGE_BASE_DETAIL}
              element={
                <ProtectedRoute>
                  <Layout>
                    <KnowledgeBaseDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.KNOWLEDGE_BASE_EDIT}
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateKnowledgeBase />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.KNOWLEDGE_BASE_UPLOAD}
              element={
                <ProtectedRoute>
                  <Layout>
                    <DocumentUpload />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* External API Routes */}
            <Route
              path={ROUTES.EXTERNAL_API}
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExternalApi />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.EXTERNAL_API_CREATE}
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateApiKey />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path={ROUTES.EXTERNAL_API_EDIT}
              element={
                <ProtectedRoute>
                  <Layout>
                    <CreateApiKey />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            
            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
